import { useCallback, useState } from "react";
import { Button, Typography, Box, Tab, Tabs } from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import CSVUploadIcon from "@mui/icons-material/CloudUploadOutlined";

import {
  tableFilterByToServerFilterBy,
  tableOrderByToServerOrderBy,
  serverEnrollmentToTableMonitor,
  useGetIssuesByMonitorDomains,
  serverStatusToTableStatus,
  serverMonitorTypeToTableMonitorType,
} from "./utils";
import { useTrueBizApi } from "../../../api";
import MonitorsTable from "../MonitorsTable";
import { TableFilterBy, TableOrderBy } from "../MonitorsTable/types";
import { useActiveMonitorsDrawer } from "../ActiveMonitorsDrawer";
import AlertsTable from "../AlertsTable";

const useGetMonitors = () => {
  const api = useTrueBizApi();

  return useCallback(
    async ({
      limit,
      offset,
      fuzzySearch,
      filterBy,
      orderBy,
    }: {
      limit: number;
      offset: number;
      fuzzySearch: string;
      filterBy: TableFilterBy[];
      orderBy: TableOrderBy[];
    }) => {
      const monitors = await api.getMonitoringEnrollments({
        limit,
        offset,
        search: fuzzySearch,
        filterBy: [...tableFilterByToServerFilterBy(filterBy)],
        orderBy: tableOrderByToServerOrderBy(orderBy),
      });

      return {
        ...monitors,
        items: monitors.enrollments.map(serverEnrollmentToTableMonitor),
      };
    },
    [api]
  );
};

const useGetUniqueValuesForAlerts = () => {
  const api = useTrueBizApi();

  return useCallback(async () => {
    return api
      .getAnomaliesMetaUniqueValuesForAlerts({
        filterBy: [],
      })
      .then((alerts) => alerts.map((a) => a.type));
  }, [api]);
};

const useGetUniqueValuesForDomain = () => {
  const api = useTrueBizApi();

  return useCallback(async () => {
    return api.getAnomaliesMetaUniqueValuesForDomain({
      filterBy: [],
    });
  }, [api]);
};

const useGetUniqueValuesForMonitorTypes = () => {
  const api = useTrueBizApi();
  return useCallback(async () => {
    return api
      .getMonitoringEnrollmentsMetaUniqueValuesForMonitorTypes({
        filterBy: [],
      })
      .then((types) => types.map(serverMonitorTypeToTableMonitorType));
  }, [api]);
};

const useGetUniqueValuesForStatus = () => {
  const api = useTrueBizApi();

  return useCallback(async () => {
    return api
      .getMonitoringEnrollmentsMetaUniqueValuesForStatus({
        filterBy: [],
      })
      .then((statuses) => statuses.map(serverStatusToTableStatus));
  }, [api]);
};

const useGetUniqueValuesForAssignees = () => {
  const api = useTrueBizApi();

  return useCallback(
    () =>
      api
        .getAnomaliesMetaAssignableUsers()
        .then(
          (users) =>
            [
              "unassigned",
              ...users.map((user) => user.email).filter((x) => !!x),
            ] as string[]
        ),
    [api]
  );
};

const useGetUniqueValuesForExternalRefId = () => {
  const api = useTrueBizApi();

  return useCallback(async () => {
    return api.getMonitoringEnrollmentsMetaUniqueValuesForExternalRefId({
      filterBy: [],
    });
  }, [api]);
};

export interface Props {
  onOpenMonitorDetail: (monitorId: UUID, issueId?: UUID) => void;
}

export default function MonitorPortfolio({ onOpenMonitorDetail }: Props) {
  const api = useTrueBizApi();

  const [activeTab, setActiveTab] = useState("alerts");

  const getMonitors = useGetMonitors();
  const getIssuesByMonitorDomains = useGetIssuesByMonitorDomains();
  const getUniqueValuesForAssignees = useGetUniqueValuesForAssignees();
  const getUniqueValuesForAlerts = useGetUniqueValuesForAlerts();
  const getUniqueValuesForDomain = useGetUniqueValuesForDomain();
  const getUniqueValuesForMonitorTypes = useGetUniqueValuesForMonitorTypes();
  const getUniqueValuesForStatus = useGetUniqueValuesForStatus();
  const getUniqueValuesForExternalRefId = useGetUniqueValuesForExternalRefId();

  const { createMonitor, csvBatchCreateEditMonitors } =
    useActiveMonitorsDrawer();

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            display: "flex",
            fontWeight: 700,
          }}
        >
          Monitoring
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 3 }}>
          <Button
            startIcon={<CSVUploadIcon />}
            onClick={() => {
              csvBatchCreateEditMonitors(true);
            }}
            disabled={false}
          >
            Upload CSV
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              createMonitor({}, true);
            }}
            disabled={false}
          >
            Enroll a Merchant
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange} centered>
            <Tab
              label="Open Alerts"
              value="alerts"
              sx={{ fontSize: "1.2rem", fontWeight: 600 }}
            />
            <Tab
              label="Monitored Businesses"
              value="monitors"
              sx={{ fontSize: "1.2rem", fontWeight: 600 }}
            />
          </Tabs>
        </Box>
        {activeTab === "alerts" ? (
          <AlertsTable
            key="open alerts"
            description="open alerts"
            getMonitors={getMonitors}
            getIssuesByMonitorDomains={getIssuesByMonitorDomains}
            getUniqueValuesForAssignees={getUniqueValuesForAssignees}
            getUniqueValuesForAlerts={getUniqueValuesForAlerts}
            getUniqueValuesForDomain={getUniqueValuesForDomain}
            getUniqueValuesForExternalRefId={getUniqueValuesForExternalRefId}
            onAssignMonitorIssuesToUser={async ({ monitorIds, userId }) => {
              await api.monitoringEnrollmentsMetaBulkAssignToUser(
                monitorIds,
                userId
              );
            }}
            onOpenMonitorDetail={onOpenMonitorDetail}
          />
        ) : (
          <MonitorsTable
            key="monitor portfolio"
            description="monitor portfolio"
            selectable
            selectionActions={["export", "pause", "resume"]}
            getMonitors={getMonitors}
            getIssuesByMonitorDomains={getIssuesByMonitorDomains}
            getUniqueValuesForDomain={getUniqueValuesForDomain}
            getUniqueValuesForExternalRefId={getUniqueValuesForExternalRefId}
            getUniqueValuesForMonitorTypes={getUniqueValuesForMonitorTypes}
            getUniqueValuesForStatus={getUniqueValuesForStatus}
            onOpenMonitorDetail={onOpenMonitorDetail}
          />
        )}
      </Box>
    </>
  );
}
